import {useNavigate, useParams} from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {Autocomplete, Box, Button, Divider, Paper, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {Printout, PrintoutOrientation, PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import SettingsEntityHeader from "../../system/layout/SettingsEntityHeader";
import {isRequiredTextMissing, isRequiredValueMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {getNameForLanguage, ISO_LANGS} from "crqlar-react-common/dist/utils/translations.util";
import PrintoutDocumentRenderer from "../print/PrintoutDocumentRenderer";
import {usePrintoutPreviewContextState} from "../print/usePrintoutPreviewContext";
import {
    getPrintoutLanguages,
    getPrintoutHeadingFontFamily,
    resolvePrintoutDocument,
    setPrintoutLanguages,
    setPrintoutHeadingFontFamily,
} from "../core/printoutDocument";
import PrintoutFontFields from "./PrintoutFontFields";
import {
    getPrintoutOverallWidth,
    getPrintoutPageSize,
    PRINTOUT_PAGE_GAP_BORDER_COLOR,
} from "../core/printoutPageGeometry";

const PREVIEW_DESK_COLOR = '#eef2f7';
const PREVIEW_DESK_COLOR_TRANSPARENT = 'rgba(238, 242, 247, 0)';

const COLLAPSED_PREVIEW_HEIGHT = 300;

const PrintoutDetails = () => {

    useTrackScreenVisit('system printout details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const printoutData = useEntity({
        get: async (): Promise<Printout | undefined> => id && id !== 'new'
            ? PrintoutService.getPrintoutById(id)
            // The empty starter document is the backend's, like every other printout template.
            : PrintoutService.getBlankPrintout(),
        create: PrintoutService.createPrintout,
        update: PrintoutService.updatePrintout,
        delete: (id) => PrintoutService.deletePrintout({id: [id]}),
        onDeleted: () => navigate('/system/printouts', {replace: true}),
        onSaved: (printout, wasCreateAction) => {
            if (wasCreateAction) {
                navigate(`/system/printouts/${printout.id}`, {replace: true})
            }
        },
        isValid: (printout) => !!printout.name?.trim() && !!printout.type,
        cleanupBeforeSave: (printout) => setPrintoutLanguages(
            {...printout, name: printout.name?.trim()},
            getPrintoutLanguages(printout),
        ),
    }, id)

    const printout = printoutData.item
    const setPrintout = printoutData.setItem
    const nameMissing = isRequiredTextMissing(printoutData, printout.name)
    const typeMissing = isRequiredValueMissing(printoutData, printout.type)

    const sectors = useAllSectors()
    const scopedSectors = useMemo(
        () => sectors.filter(sector => (
            sector.id && printout.scopedSectorIds?.includes(sector.id)
        )),
        [printout.scopedSectorIds, sectors],
    )

    const [previewExpanded, setPreviewExpanded] = useState(false)
    const printoutLanguages = useMemo(() => getPrintoutLanguages(printout), [printout])
    const languageOptions = useMemo(() => ISO_LANGS.map(language => language.code.toLowerCase()), [])
    const previewDocument = useMemo(
        () => resolvePrintoutDocument(printout),
        [printout],
    )
    const previewPageWidth = useMemo(
        () => getPrintoutOverallWidth(getPrintoutPageSize(printout.orientation)) + 2,
        [printout.orientation],
    )

    const previewContextState = usePrintoutPreviewContextState(
        printout.type,
        previewDocument?.language,
        printout.scopedSectorIds,
    )
    const previewContext = previewContextState.context

    const updateField = <K extends keyof Printout>(field: K, value: Printout[K]) => {
        setPrintout((current) => ({...current, [field]: value}))
    }

    const headerSection = (
        <SettingsEntityHeader
            title={printout.name}
            data={printoutData}
            back={() => navigate('/system/printouts')}
        />
    )

    const basicInfoSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={{xs: 1.5, sm: 1}}>
                <Property
                    title={t('Name')}
                    tooltip={t('Internal label for finding and managing this printout.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={printout.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    // Full width prevents Property's flex minimum from overflowing the A4 preview.
    const contentSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                gap={2}
                flexWrap={'wrap'}
            >
                <Typography variant={'h5'} textAlign={'start'}>
                    {t('Editor')}
                </Typography>

                <Button
                    variant={'contained'}
                    endIcon={<ArrowForwardIcon/>}
                    onClick={() => navigate(`/system/printouts/${printout.id}/editor`)}
                    sx={{flexShrink: 0}}
                >
                    {t('Open editor')}
                </Button>
            </Stack>

            <Stack gap={0.5}>
                <Paper
                    elevation={0}
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        width: '100%',
                        minWidth: 0,
                        bgcolor: PREVIEW_DESK_COLOR,
                    }}
                >
                    {previewDocument ? (
                        <Box sx={{position: 'relative', minWidth: 0}}>
                            <Box
                                id={'printout-details-preview'}
                                sx={{
                                    width: '100%',
                                    minWidth: 0,
                                    minHeight: previewExpanded
                                        ? 0
                                        : COLLAPSED_PREVIEW_HEIGHT,
                                    maxHeight: previewExpanded
                                        ? 'none'
                                        : COLLAPSED_PREVIEW_HEIGHT,
                                    overflow: previewExpanded ? 'visible' : 'hidden',
                                }}
                            >
                                {previewContextState.loading ? (
                                    <Box
                                        aria-label={t('Preparing preview')}
                                        role={'status'}
                                        sx={{
                                            width: '100%',
                                            maxWidth: `${previewPageWidth}px`,
                                            height: COLLAPSED_PREVIEW_HEIGHT,
                                            mx: 'auto',
                                            bgcolor: 'background.paper',
                                            border: 1,
                                            borderColor: PRINTOUT_PAGE_GAP_BORDER_COLOR,
                                            boxSizing: 'border-box',
                                            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.14)',
                                        }}
                                    />
                                ) : (
                                    <PrintoutDocumentRenderer
                                        printout={printout}
                                        context={previewContext}
                                        language={previewDocument.language}
                                    />
                                )}
                            </Box>

                            {!previewExpanded && (
                                <Box
                                    aria-hidden
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        height: 96,
                                        pointerEvents: 'none',
                                        background: `linear-gradient(to bottom, ${PREVIEW_DESK_COLOR_TRANSPARENT} 0%, ${PREVIEW_DESK_COLOR} 80%)`,
                                    }}
                                />
                            )}
                        </Box>
                    ) : (
                        <Stack alignItems={'center'} justifyContent={'center'} minHeight={180} px={2}>
                            <Typography color={'text.secondary'} textAlign={'center'}>
                                {t('Add a language below to create its empty layout.')}
                            </Typography>
                        </Stack>
                    )}
                </Paper>

                {previewDocument && (
                    <Button
                        aria-controls={'printout-details-preview'}
                        aria-expanded={previewExpanded}
                        size={'small'}
                        onClick={() => setPreviewExpanded(current => !current)}
                        sx={{alignSelf: 'flex-start', ml: 1.5, textTransform: 'none'}}
                    >
                        {previewExpanded ? t('Show less') : t('Show more')}
                    </Button>
                )}
            </Stack>
        </Stack>
    )

    const settingsSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Settings')}
            </Typography>

            <Stack gap={{xs: 1.5, sm: 1}}>
                <Property
                    title={t('Type')}
                    tooltip={t('Determines which print workflow uses this printout and which data is available.')}
                >
                    <Autocomplete
                        id={'printout-type'}
                        options={Object.values(PrintoutType)}
                        getOptionLabel={(option: PrintoutType) => option ? t(`Printout type ${option}`) : t('(Not set)')}
                        value={printout.type || null}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                                error={typeMissing}
                                helperText={typeMissing ? t('Type is required') : undefined}
                            />
                        )}
                        onChange={(e, value) => updateField('type', value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Orientation')}
                    tooltip={t('Page orientation used when this printout is rendered.')}
                >
                    <Autocomplete
                        id={'printout-orientation'}
                        options={Object.values(PrintoutOrientation)}
                        getOptionLabel={(option: PrintoutOrientation) => option ? t(`Printout orientation ${option}`) : t('(Not set)')}
                        value={printout.orientation || null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        onChange={(e, value) => updateField('orientation', value ?? undefined)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Languages')}
                    tooltip={t('Languages available for this printout. The first one is the fallback layout.')}
                >
                    <Autocomplete
                        id={'languages'}
                        multiple
                        options={languageOptions}
                        getOptionLabel={(option: string) => getNameForLanguage(option, true) || option}
                        value={printoutLanguages}
                        onChange={(e, value) => setPrintout(current => setPrintoutLanguages(current, value))}
                        renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Sectors')}
                    tooltip={t('Choose one or more sectors. Leave empty to use this printout everywhere. The first selected sector supplies sample data in the editor preview.')}
                >
                    <Autocomplete
                        id={'scoped-sector-ids'}
                        multiple
                        options={sectors}
                        getOptionLabel={(option) => option.name || ''}
                        value={scopedSectors}
                        renderInput={(params) => <TextField {...params} fullWidth size={'small'}/>}
                        onChange={(e, value) => updateField(
                            'scopedSectorIds',
                            value.map(sector => sector.id).filter((id): id is string => Boolean(id)),
                        )}
                        fullWidth
                    />
                </Property>

            </Stack>
        </Stack>
    )

    const fontSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Font')}
            </Typography>

            <PrintoutFontFields
                hasLayouts={printoutLanguages.length > 0}
                value={{
                    fontUrl: printout.fontUrl,
                    fontFamily: printout.fontFamily,
                    headingFontFamily: getPrintoutHeadingFontFamily(printout),
                }}
                onChange={(font) => setPrintout((current) => setPrintoutHeadingFontFamily({
                    ...current,
                    fontUrl: font.fontUrl,
                    fontFamily: font.fontFamily,
                }, font.headingFontFamily))}
            />
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete printout')}
                description={t('This permanently deletes the printout. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={printoutData.saving}
                onDelete={() => printoutData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {printout.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {basicInfoSection}

                {
                    !!printout.id &&
                    <>
                        <Divider/>
                        {contentSection}
                    </>
                }

                <Divider/>
                {settingsSection}

                {
                    !printoutData.loading &&
                    <>
                        <Divider/>
                        {fontSection}
                    </>
                }

                {
                    !!printout.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default PrintoutDetails;
